/**
 * AIA Design System Tokens
 * All design values centralized for consistency across the app
 */

export const aiaTheme = {
  colors: {
    // Brand colors
    brandPrimary: '#E60012', // AIA Red
    brandOnPrimary: '#FFFFFF',
    brandSecondary: '#00539F', // AIA Blue
    brandOnSecondary: '#FFFFFF',

    // Surface colors
    surface: '#FFFFFF',
    surfaceAlt: '#F5F5F5',
    surfaceElevated: '#FFFFFF',
    background: '#FAFAFA',

    // Text colors
    textPrimary: '#1A1A1A',
    textSecondary: '#666666',
    textTertiary: '#999999',
    textDisabled: '#CCCCCC',
    textOnPrimary: '#FFFFFF',
    textOnSecondary: '#FFFFFF',

    // Border & divider
    border: '#E0E0E0',
    borderLight: '#F0F0F0',
    divider: '#EEEEEE',

    // Semantic colors
    success: '#00A651',
    successLight: '#E8F5E9',
    warning: '#FF9800',
    warningLight: '#FFF3E0',
    error: '#D32F2F',
    errorLight: '#FFEBEE',
    info: '#2196F3',
    infoLight: '#E3F2FD',

    // Neutral scale
    neutral50: '#FAFAFA',
    neutral100: '#F5F5F5',
    neutral200: '#EEEEEE',
    neutral300: '#E0E0E0',
    neutral400: '#BDBDBD',
    neutral500: '#9E9E9E',
    neutral600: '#757575',
    neutral700: '#616161',
    neutral800: '#424242',
    neutral900: '#212121',

    // Overlay
    overlay: 'rgba(0, 0, 0, 0.5)',
    overlayLight: 'rgba(0, 0, 0, 0.3)',
    overlayDark: 'rgba(0, 0, 0, 0.7)',

    // Special
    transparent: 'transparent',
  },

  typography: {
    // Display (Hero headings)
    display: {
      fontSize: 32,
      lineHeight: 40,
      fontWeight: '700' as const,
      letterSpacing: -0.5,
    },
    // Headlines
    headline: {
      fontSize: 24,
      lineHeight: 32,
      fontWeight: '600' as const,
      letterSpacing: -0.3,
    },
    // Titles
    title: {
      fontSize: 20,
      lineHeight: 28,
      fontWeight: '600' as const,
      letterSpacing: 0,
    },
    titleMedium: {
      fontSize: 18,
      lineHeight: 24,
      fontWeight: '600' as const,
      letterSpacing: 0,
    },
    // Body text
    body: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: '400' as const,
      letterSpacing: 0,
    },
    bodyMedium: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: '400' as const,
      letterSpacing: 0,
    },
    bodySmall: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: '400' as const,
      letterSpacing: 0,
    },
    // Caption
    caption: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: '400' as const,
      letterSpacing: 0.4,
    },
    // Button text
    button: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: '600' as const,
      letterSpacing: 0.5,
    },
    buttonSmall: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: '600' as const,
      letterSpacing: 0.5,
    },
  },

  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    base: 16,
    lg: 20,
    xl: 24,
    xxl: 32,
    xxxl: 48,
  },

  radius: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    full: 9999,
  },

  elevation: {
    none: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
      elevation: 0,
    },
    sm: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    md: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.12,
      shadowRadius: 4,
      elevation: 4,
    },
    lg: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 8,
      elevation: 8,
    },
    xl: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.18,
      shadowRadius: 16,
      elevation: 12,
    },
  },

  motion: {
    // Timing
    duration: {
      fast: 150,
      normal: 250,
      slow: 350,
    },
    // Easing
    easing: {
      standard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
      decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
      accelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
    },
  },

  // Accessibility
  minTouchTarget: 44,

  // Layout
  layout: {
    screenPadding: 16,
    cardPadding: 16,
    sectionSpacing: 24,
  },
} as const;

export type Theme = typeof aiaTheme;
export type ThemeColors = keyof Theme['colors'];
export type ThemeSpacing = keyof Theme['spacing'];
export type ThemeRadius = keyof Theme['radius'];

// Helper to convert elevation object to CSS string for styled-components
export const getElevationStyle = (elevation: typeof aiaTheme.elevation.sm) => `
  shadow-color: ${elevation.shadowColor};
  shadow-offset: ${elevation.shadowOffset.width}px ${elevation.shadowOffset.height}px;
  shadow-opacity: ${elevation.shadowOpacity};
  shadow-radius: ${elevation.shadowRadius}px;
  elevation: ${elevation.elevation};
`;
